import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dna,
  Plus,
  MagnifyingGlass,
  DotsThree,
  Eye,
  PencilSimple,
  Trash,
  GitBranch,
  ArrowLeft,
  FloppyDisk,
  Tag,
  X,
  UploadSimple
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import ImportModal from '../../components/ImportModal';
import FileAttachments from '../../components/FileAttachments';

// Plasmids List Page
export const PlasmidsPage = () => {
  const navigate = useNavigate();
  const [plasmids, setPlasmids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadPlasmids();
  }, []);

  const loadPlasmids = async () => {
    try {
      const data = await api.getPlasmids();
      setPlasmids(data);
    } catch (error) {
      console.error('Failed to load plasmids:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this plasmid?')) {
      try {
        await api.deletePlasmid(id);
        setPlasmids(plasmids.filter(p => p.id !== id));
        toast.success('Plasmid deleted');
      } catch (error) {
        toast.error('Failed to delete plasmid');
      }
    }
  };

  const filteredPlasmids = plasmids.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="plasmids-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Plasmids</h1>
          <p className="text-zinc-500 mt-1">DNA construct library with lineage tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={() => setShowImport(true)} data-testid="import-plasmids-btn">
            <UploadSimple size={16} /> Import
          </Button>
          <Link to="/plasmids/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="new-plasmid-btn">
              <Plus size={16} /> New Plasmid
            </Button>
          </Link>
        </div>
      </div>

      <div className="card-flat p-4 mb-6">
        <div className="relative max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            type="text"
            placeholder="Search plasmids..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 input-field w-full"
            data-testid="search-plasmids"
          />
        </div>
      </div>

      {filteredPlasmids.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPlasmids.map((plasmid) => (
            <div key={plasmid.id} className="card-flat p-4 hover:border-zinc-300" data-testid={`plasmid-card-${plasmid.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <Link to={`/plasmids/${plasmid.id}`} className="heading-card text-slate-100 hover:text-indigo-300 block truncate">
                    {plasmid.name}
                  </Link>
                  <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{plasmid.description || 'No description'}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 hover:bg-zinc-100 rounded-sm"><DotsThree size={20} /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/plasmids/${plasmid.id}`)}>
                      <Eye size={16} className="mr-2" /> View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/plasmids/${plasmid.id}/lineage`)}>
                      <GitBranch size={16} className="mr-2" /> Lineage
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/plasmids/${plasmid.id}/edit`)}>
                      <PencilSimple size={16} className="mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDelete(plasmid.id)} className="text-red-400">
                      <Trash size={16} className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {plasmid.sequence && (
                <div className="mt-3 text-xs text-zinc-500">
                  {plasmid.sequence.length} bp
                </div>
              )}
              {plasmid.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {plasmid.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="text-xs bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-sm">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state card-flat">
          <Dna size={48} className="text-zinc-300 mb-4" />
          <h3 className="heading-card text-slate-100 mb-2">No plasmids yet</h3>
          <p className="text-sm text-zinc-500 mb-4">Start building your construct library</p>
          <Link to="/plasmids/new"><Button className="btn-primary">Add Plasmid</Button></Link>
        </div>
      )}
      {showImport && <ImportModal type="plasmids" onClose={() => setShowImport(false)} onImportComplete={loadPlasmids} />}
    </div>
  );
};

// Plasmid Form
export const PlasmidForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [plasmids, setPlasmids] = useState([]);
  const [strains, setStrains] = useState([]);
  const [tagInput, setTagInput] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sequence: '',
    features: [],
    parent_id: '',
    strain_id: '',
    tags: []
  });

  useEffect(() => {
    loadOptions();
    if (isEdit && id) loadPlasmid();
  }, [id, isEdit]);

  const loadOptions = async () => {
    try {
      const [plasmidsData, strainsData] = await Promise.all([
        api.getPlasmids(),
        api.getStrains()
      ]);
      setPlasmids(plasmidsData);
      setStrains(strainsData);
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const loadPlasmid = async () => {
    try {
      const data = await api.getPlasmid(id);
      setFormData(data);
    } catch (error) {
      toast.error('Failed to load plasmid');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.updatePlasmid(id, formData);
        toast.success('Plasmid updated');
      } else {
        const result = await api.createPlasmid(formData);
        toast.success('Plasmid created');
        navigate(`/plasmids/${result.id}`);
      }
    } catch (error) {
      toast.error('Failed to save plasmid');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="plasmid-form-page">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/plasmids" className="p-2 hover:bg-zinc-100 rounded-sm"><ArrowLeft size={20} /></Link>
        <div>
          <h1 className="heading-display">{isEdit ? 'Edit Plasmid' : 'New Plasmid'}</h1>
          <p className="text-zinc-500 mt-1">{isEdit ? 'Update plasmid details' : 'Add a new plasmid'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-flat p-6 space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="pXYZ-123" className="input-field w-full mt-1" required data-testid="plasmid-name-input" />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" name="description" value={formData.description} onChange={handleChange} placeholder="Describe the plasmid..." className="input-field w-full mt-1" />
              </div>
              <div>
                <Label htmlFor="sequence">Sequence</Label>
                <Textarea id="sequence" name="sequence" value={formData.sequence} onChange={(e) => setFormData({ ...formData, sequence: e.target.value.toUpperCase().replace(/[^ATCG]/gi, '') })} placeholder="ATCGATCG..." className="input-field w-full mt-1 min-h-[150px] font-mono text-sm" />
                {formData.sequence && <p className="text-xs text-zinc-500 mt-1">{formData.sequence.length} bp</p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="parent_id">Parent Plasmid (for lineage)</Label>
                  <select id="parent_id" name="parent_id" value={formData.parent_id || ''} onChange={handleChange} className="input-field w-full mt-1">
                    <option value="">None (original construct)</option>
                    {plasmids.filter(p => p.id !== id).map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="strain_id">Host Strain</Label>
                  <select id="strain_id" name="strain_id" value={formData.strain_id || ''} onChange={handleChange} className="input-field w-full mt-1">
                    <option value="">Select strain</option>
                    {strains.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card-flat p-6">
              <Label>Tags</Label>
              <div className="flex gap-2 mt-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} placeholder="Add tag" className="input-field flex-1" />
                <Button type="button" variant="outline" onClick={handleAddTag}><Tag size={16} /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 px-2 py-1 text-sm rounded-sm">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-400"><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>

            <Button type="submit" className="btn-primary w-full" disabled={saving} data-testid="save-plasmid-btn">
              {saving ? <><div className="spinner w-4 h-4 mr-2" />Saving...</> : <><FloppyDisk size={16} className="mr-2" />{isEdit ? 'Update' : 'Create'} Plasmid</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Plasmid Lineage View
export const PlasmidLineage = () => {
  const { id } = useParams();
  const [plasmid, setPlasmid] = useState(null);
  const [lineage, setLineage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [plasmidData, lineageData] = await Promise.all([
        api.getPlasmid(id),
        api.getPlasmidLineage(id)
      ]);
      setPlasmid(plasmidData);
      setLineage(lineageData);
    } catch (error) {
      toast.error('Failed to load lineage');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="plasmid-lineage-page">
      <div className="flex items-center gap-4 mb-6">
        <Link to={`/plasmids/${id}`} className="p-2 hover:bg-zinc-100 rounded-sm"><ArrowLeft size={20} /></Link>
        <div>
          <h1 className="heading-display">{plasmid?.name} - Lineage</h1>
          <p className="text-zinc-500 mt-1">Construct derivation history</p>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="flex flex-col items-center gap-2">
          {/* Ancestors */}
          {lineage?.ancestors?.slice().reverse().map((ancestor, idx) => (
            <React.Fragment key={ancestor.id}>
              <Link to={`/plasmids/${ancestor.id}`} className="lineage-node hover:border-blue-300 transition-colors">
                <div className="font-medium">{ancestor.name}</div>
                <div className="text-xs text-zinc-500">{new Date(ancestor.created_at).toLocaleDateString()}</div>
              </Link>
              <div className="lineage-connector" />
            </React.Fragment>
          ))}

          {/* Current */}
          {lineage?.current && (
            <div className="lineage-node current">
              <div className="font-medium">{lineage.current.name}</div>
              <div className="text-xs text-indigo-400">Current</div>
            </div>
          )}

          {/* Children */}
          {lineage?.children?.length > 0 && (
            <>
              <div className="lineage-connector" />
              <div className="flex gap-4 flex-wrap justify-center">
                {lineage.children.map(child => (
                  <Link key={child.id} to={`/plasmids/${child.id}`} className="lineage-node hover:border-blue-300 transition-colors">
                    <div className="font-medium">{child.name}</div>
                    <div className="text-xs text-zinc-500">{new Date(child.created_at).toLocaleDateString()}</div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {isEdit && id && (
        <div className="card-flat p-5 mt-4">
          <FileAttachments entityType="plasmids" entityId={id} />
        </div>
      )}
    </div>
  );
};

export default PlasmidsPage;
